import { useForm } from '@conform-to/react';
import { getZodConstraint, parseWithZod } from '@conform-to/zod';
import { redirect, type ActionFunctionArgs } from '@remix-run/node';
import { useFetcher } from '@remix-run/react';
import { typedjson } from 'remix-typedjson';
import { ForgotPasswordEmail } from '#common/emails/reset-password.js';
import { ForgotPassword, forgotPasswordSchema } from '#common/features/auth/ForgotPassword/index.js';
import { useFormGlobalErrorToast } from '#common/hooks/useFormGlobalErrorToast.js';
import { getRedirectToUrl, prepareVerification } from '#common/models/verify.server.js';
import { sendEmail } from '#common/services/email.server.js';
import { checkHoneypot } from '#common/utils/honeypot.server.js';
import { getUserByEmail } from '#speaker-admin/models/user.server.js';

export const action = async ({ request }: ActionFunctionArgs) => {
    const formData = await request.formData();
    await checkHoneypot(formData);

    const submission = parseWithZod(formData, { schema: forgotPasswordSchema });
    if (submission.status !== 'success') {
        return typedjson(
            { result: submission.reply() },
            { status: submission.status === 'error' ? 400 : 200 }
        );
    }

    const user = await getUserByEmail(submission.value.email);

    if (!user) {
        const redirectTo = getRedirectToUrl({ request, type: 'reset-password', target: submission.value.email });
        return redirect(redirectTo.toString());
    }

    const { otp, redirectTo, verifyUrl } = await prepareVerification({
        period: 10 * 60,
        request,
        type: 'reset-password',
        target: user.email
    });

    const res = await sendEmail({
        to: user.email,
        subject: 'CrispyVoice Password Reset',
        react: <ForgotPasswordEmail onboardingUrl={verifyUrl.toString()} otp={otp} />
    });

    if (res.status === 'success') {
        return redirect(redirectTo.toString());
    } else {
        return typedjson(
            submission.reply({ formErrors: [ res.error.message ] }),
            { status: 500 }
        );
    }
};

export default function ForgotPasswordPage() {
    const forgotPassword = useFetcher<typeof action>();
    const [ form, fields ] = useForm({
        lastResult: forgotPassword.data,
        constraint: getZodConstraint(forgotPasswordSchema),
        shouldValidate: 'onBlur',
        shouldRevalidate: 'onInput',
        onValidate({ formData }) {
            return parseWithZod(formData, { schema: forgotPasswordSchema });
        }
    });
    useFormGlobalErrorToast(form);

    return (
        <>
            <ForgotPassword fields={fields} forgotPasswordFetcher={forgotPassword} form={form} />
        </>
    );
}
